import React from "react";
import { Box, IconButton, Modal, Typography, styled } from "@mui/material";
import { RichText } from "./RichText";

// Inline close icon SVG (eva:close-fill equivalent)
const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="m13.41 12l4.3-4.29a1 1 0 1 0-1.42-1.42L12 10.59l-4.29-4.3a1 1 0 0 0-1.42 1.42l4.3 4.29l-4.3 4.29a1 1 0 0 0 0 1.42a1 1 0 0 0 1.42 0l4.29-4.3l4.29 4.3a1 1 0 0 0 1.42 0a1 1 0 0 0 0-1.42Z"
    />
  </svg>
);

// Bullet PNG embedded as data URL (24x24 RGBA, 345 bytes)
const bulletDataUrl =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAA" +
  "AXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAADuSURBVHgB7ZPNDQFBGIaf2bi5KIEO6IAOSJA4" +
  "iQ6UsCqgg+VMIjpQgg6owcENGZ852Z+x+yV7Yp/DTvLN97dv5oWKn8dkBe2WtRxT8qsv8g3NkI0/" +
  "Jdl8Rx/LnuJcqdExAzcsRZCKWCnQ0eDuXyg1wIw4ynFEg6Etsi4LDXA8mEmR9k/mMqRbaICZiJ6W" +
  "BVoMkY1EsrwBLnfESo4TGixN6vHXF/B9owZagri0NV+e6Bm6jTRYkfbG4TOUbbS9NH5wRosVP4zj" +
  "svpeUYSeMNn8TRlOdtJI81bWVRlOvvKk57v0OXlF0eaGufNNxf/yAqEmP2P8bIuiAAAAAElFTkSu" +
  "QmCC";

export interface FacetDefinition {
  facetCode: string;
  longLabel: string;
  icon?: string;
  color?: string;
  secondaryColor?: string;
  description?: unknown;
  variable?: string;
  facet?: string;
  iconifyIcon?: string;
}

export interface FacetInfoModalProps {
  open: boolean;
  onClose: () => void;
  facet: FacetDefinition | null;
  renderFallbackIcon?: (
    facet: FacetDefinition,
    size: number,
  ) => React.ReactNode;
}

export const FacetInfoModal = ({
  open,
  onClose,
  facet,
  renderFallbackIcon,
}: FacetInfoModalProps) => {
  if (!facet) return null;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="facet-info-modal">
      <ModalContent>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>

        <ModalHeader>
          {facet.icon ? (
            <FacetIcon src={facet.icon} alt={facet.variable} />
          ) : renderFallbackIcon ? (
            renderFallbackIcon(facet, 64)
          ) : null}
          <Typography variant="h4" component="h2">
            {facet.longLabel}
          </Typography>
        </ModalHeader>

        <ModalBody>
          <RichText value={facet.description as string | undefined} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled(Box)({
  label: "FacetInfoModalContent",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "white",
  borderRadius: "16px",
  padding: "32px",
  width: "90%",
  border: "none",
  outline: "none",
  maxWidth: "480px",
  maxHeight: "90vh",
  overflowY: "auto",
  "& ul": {
    paddingLeft: 0,
    paddingRight: "20px",
    listStyle: "none",
    "& li": {
      backgroundImage: `url("${bulletDataUrl}")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "left 4px",
      backgroundSize: "12px",
      paddingLeft: "20px",
    },
  },
});

const CloseButton = styled(IconButton)({
  label: "FacetInfoCloseButton",
  position: "absolute",
  top: "16px",
  right: "16px",
});

const ModalHeader = styled(Box)({
  label: "FacetInfoModalHeader",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: "16px",
  marginBottom: "24px",
});

const ModalBody = styled(Box)({ label: "FacetInfoModalBody" });

const FacetIcon = styled("img")({
  label: "FacetInfoIcon",
  width: "64px",
  height: "64px",
});
